import { directSupabase as supabase } from '../../lib/supabaseClient';
import { isFreeAgentSyncEnabled } from '../../config/freeagentConfig';
import { isAuthenticated as isFreeAgentConnected } from '../../freeagent/auth';
import * as freeAgentApi from '../../freeagent/api';
import { ensureFreeAgentAccountContact } from '../../integrations/freeagentSync';
import { billToService, type BillToCompany } from '../../services/billToService';
import type { DirectoryAccount } from './accounts.model';
import { accountsService } from './accounts.service';
import {
  FREEAGENT_CIS_BANDS,
  MAX_BILL_ATTACHMENT_BYTES,
  billCategoryProblem,
  buildBillHtml,
  buildFreeAgentBillPayload,
  buildFreeAgentBillUpdatePayload,
  cisDeductionMismatch,
  freeAgentAttachmentContentType,
  fromDbBill,
  legacyBillTo,
  toDbBill,
  validateBillInput,
  type AccountBill,
  type AccountBillInput,
  type BillCategoryUrls,
  type BillToParty,
} from './bills.model';

export const BILL_FILES_BUCKET = 'account-bill-files';

function errorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  const row = error as { message?: string; details?: string; code?: string };
  if (row.code === '23505') return 'A bill with that invoice number is already recorded for this account.';
  if (row.code === '42P01') return 'Bills are not set up in the database yet. Apply the account bills migration.';
  if (row.code === '42703') return 'Bills need the subcontractor migration. Apply 20261015100000-bills-subcontractor-migration.sql.';
  return row.message || row.details || fallback;
}

async function currentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error || new Error('Could not read the attachment'));
    reader.readAsDataURL(blob);
  });
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function isNotFound(err: unknown): boolean {
  return (err as { status?: number })?.status === 404;
}

/** What the PDF and the bill page show about who the bill is from and to. */
export type BillParties = {
  account: DirectoryAccount | null;
  billTo: BillToParty | null;
};

function billToParty(company: BillToCompany): BillToParty {
  return {
    name: company.name,
    address: company.address,
    vatNumber: company.vat_number,
    companyNumber: company.company_number,
    paymentTerms: company.payment_terms,
  };
}

async function updateRow(id: string, patch: Record<string, unknown>, fallback: string): Promise<AccountBill> {
  const { data, error } = await supabase.from('account_bills').update(patch).eq('id', id).select('*').single();
  if (error) throw new Error(errorMessage(error, fallback));
  return fromDbBill(data);
}

export const billsService = {
  async list(): Promise<AccountBill[]> {
    const { data, error } = await supabase
      .from('account_bills')
      .select('*')
      .order('dated_on', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) throw new Error(errorMessage(error, 'Failed to load bills'));
    return (data || []).map(fromDbBill);
  },

  async get(id: string): Promise<AccountBill | null> {
    const { data, error } = await supabase.from('account_bills').select('*').eq('id', id).maybeSingle();
    if (error) throw new Error(errorMessage(error, 'Failed to load the bill'));
    return data ? fromDbBill(data) : null;
  },

  async billToCompanies(): Promise<BillToCompany[]> {
    return billToService.listActive();
  },

  async parties(bill: AccountBill): Promise<BillParties> {
    const [account, companies] = await Promise.all([
      accountsService.get(bill.accountId),
      bill.billToCompanyId ? billToService.listActive().catch(() => [] as BillToCompany[]) : Promise.resolve([] as BillToCompany[]),
    ]);
    const company = companies.find((row) => row.id === bill.billToCompanyId);
    return { account, billTo: company ? billToParty(company) : legacyBillTo(bill) };
  },

  async create(input: AccountBillInput): Promise<AccountBill> {
    const problem = validateBillInput(input);
    if (problem) throw new Error(problem);
    const { data, error } = await supabase
      .from('account_bills')
      .insert({ ...toDbBill(input), created_by: await currentUserId() })
      .select('*')
      .single();
    if (error) throw new Error(errorMessage(error, 'Failed to save the bill'));
    return fromDbBill(data);
  },

  /**
   * Saves changes. A bill already in FreeAgent is flagged so the change is sent there next;
   * its CIS band cannot change because FreeAgent fixes the rate when the bill is created.
   */
  async update(id: string, input: AccountBillInput): Promise<AccountBill> {
    const existing = await this.get(id);
    if (!existing) throw new Error('Bill not found');
    if (existing.status === 'archived') throw new Error('Archived bills are the record of the old subcontractor invoices and cannot be changed.');
    if (existing.freeagentBillUrl && existing.paidOn) throw new Error('This bill is paid in FreeAgent, so it can no longer be changed.');
    if (existing.freeagentBillUrl && input.cisBand !== existing.cisBand) {
      throw new Error('FreeAgent fixes the CIS rate when a bill is created. To change the CIS band, delete this bill and enter it again.');
    }
    const problem = validateBillInput(input);
    if (problem) throw new Error(problem);
    return updateRow(id, {
      ...toDbBill(input),
      ...(existing.freeagentBillUrl ? { freeagent_out_of_date: true } : {}),
    }, 'Failed to save the bill');
  },

  /** Deletes the bill, removing it from FreeAgent first when it was sent. */
  async remove(bill: AccountBill): Promise<void> {
    if (bill.status === 'archived') throw new Error('Archived bills are the record of the old subcontractor invoices and are kept.');
    if (bill.freeagentBillUrl) {
      if (bill.paidOn) throw new Error('This bill is paid in FreeAgent. Remove the payment there before deleting it.');
      await this.assertFreeAgentReady();
      try {
        await freeAgentApi.deleteBill(bill.freeagentBillUrl);
      } catch (err) {
        if (!isNotFound(err)) throw new Error(`FreeAgent would not delete the bill: ${errorMessage(err, 'unknown error')}`);
      }
    }
    const { error } = await supabase.from('account_bills').delete().eq('id', bill.id);
    if (error) throw new Error(errorMessage(error, 'Failed to delete the bill'));
    if (bill.attachmentPath) {
      await supabase.storage.from(BILL_FILES_BUCKET).remove([bill.attachmentPath]);
    }
  },

  /** For bills kept out of FreeAgent; bills in FreeAgent are paid there and picked up by refresh. */
  async markPaid(bill: AccountBill, paidOn: string = today()): Promise<AccountBill> {
    if (bill.freeagentBillUrl) throw new Error('Record the payment in FreeAgent as a Bill Payment, then refresh.');
    return updateRow(bill.id, { paid_on: paidOn }, 'Failed to mark the bill paid');
  },

  async unmarkPaid(bill: AccountBill): Promise<AccountBill> {
    if (bill.freeagentBillUrl) throw new Error('This bill’s payment is recorded in FreeAgent.');
    return updateRow(bill.id, { paid_on: null }, 'Failed to update the bill');
  },

  /** Stores the supplier's PDF or image against the bill, replacing any earlier file. */
  async attach(bill: AccountBill, file: File): Promise<AccountBill> {
    if (!freeAgentAttachmentContentType(file.type)) throw new Error('Attach a PDF, PNG, JPEG or GIF.');
    if (file.size > MAX_BILL_ATTACHMENT_BYTES) throw new Error('Attachments must be 5 MB or smaller, which is FreeAgent’s limit.');
    const safeName = file.name.replace(/[^A-Za-z0-9._-]+/g, '_');
    const path = `${bill.accountId}/${bill.id}/${Date.now()}-${safeName}`;
    const { error: uploadError } = await supabase.storage
      .from(BILL_FILES_BUCKET)
      .upload(path, file, { contentType: file.type, upsert: false });
    if (uploadError) throw new Error(errorMessage(uploadError, 'Failed to upload the attachment'));
    const { data, error } = await supabase
      .from('account_bills')
      .update({
        attachment_path: path,
        attachment_name: file.name,
        attachment_content_type: file.type,
        attachment_size: file.size,
      })
      .eq('id', bill.id)
      .select('*')
      .single();
    if (error) {
      await supabase.storage.from(BILL_FILES_BUCKET).remove([path]);
      throw new Error(errorMessage(error, 'Failed to save the attachment'));
    }
    if (bill.attachmentPath && bill.attachmentPath !== path) {
      await supabase.storage.from(BILL_FILES_BUCKET).remove([bill.attachmentPath]);
    }
    return fromDbBill(data);
  },

  async attachmentUrl(bill: AccountBill): Promise<string | null> {
    if (!bill.attachmentPath) return null;
    const { data, error } = await supabase.storage.from(BILL_FILES_BUCKET).createSignedUrl(bill.attachmentPath, 300);
    if (error) throw new Error(errorMessage(error, 'Failed to open the attachment'));
    return data?.signedUrl || null;
  },

  /** Our write-up of the bill as a PDF, from the account's details and the bill-to company. */
  async pdfBlob(bill: AccountBill, parties?: BillParties): Promise<Blob> {
    const { account, billTo } = parties || await this.parties(bill);
    const issuerName = account?.companyName || String(bill.legacySnapshot?.issued_by || '') || 'Subcontractor';
    const html = buildBillHtml({
      bill,
      issuer: account ? { ...account, logoUrl: account.logoUrl || bill.legacySnapshot?.logo_url || null } : { companyName: issuerName },
      billTo,
      origin: window.location.origin,
    });
    const response = await fetch('/api/export-pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html, pdfOptions: { margin: { top: '18mm', right: '18mm', bottom: '18mm', left: '18mm' } } }),
    });
    if (!response.ok) {
      const message = await response.text().catch(() => '');
      throw new Error(message || `PDF export failed (${response.status})`);
    }
    return response.blob();
  },

  async downloadPdf(bill: AccountBill): Promise<void> {
    const parties = await this.parties(bill);
    const blob = await this.pdfBlob(bill, parties);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${parties.account?.companyName || 'Bill'} - Invoice ${bill.reference}.pdf`.replace(/[\\/:*?"<>|]+/g, '-');
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  async freeAgentCategories(): Promise<freeAgentApi.FreeAgentCategory[]> {
    return freeAgentApi.listCostOfSalesCategories();
  },

  async assertFreeAgentReady(): Promise<void> {
    if (!isFreeAgentSyncEnabled()) throw new Error('FreeAgent sync is not enabled.');
    if (!(await isFreeAgentConnected())) throw new Error('Connect to FreeAgent first (Admin → FreeAgent).');
  },

  /**
   * Creates the bill in FreeAgent, or updates the one already there. For CIS bills the contact is
   * set to the bill's band first and the lines go to the CIS categories, so FreeAgent records the
   * gross cost and takes the deduction itself; its figure is then checked against ours.
   */
  async sendToFreeAgent(billId: string, categories: BillCategoryUrls): Promise<AccountBill> {
    await this.assertFreeAgentReady();
    const bill = await this.get(billId);
    if (!bill) throw new Error('Bill not found');
    if (bill.status === 'archived') {
      throw new Error('Archived bills are already in FreeAgent as subcontractor payments and are not sent again.');
    }
    if (bill.freeagentBillUrl && !bill.freeagentOutOfDate) return bill;
    const account = await accountsService.get(bill.accountId);
    if (!account) throw new Error('The account on this bill no longer exists.');

    const categoryList = await freeAgentApi.listCostOfSalesCategories();
    const categoryProblem = billCategoryProblem({ cisBand: bill.cisBand, lines: bill.lines, chosen: categories, categories: categoryList });
    if (categoryProblem) throw new Error(categoryProblem);
    if (bill.cisBand !== 'none') {
      if (!account.utrNumber) throw new Error(`Add ${account.companyName}’s UTR to their account before sending a CIS bill.`);
      if (!(await freeAgentApi.isCisContractor())) {
        throw new Error('CIS for Contractors is not switched on in FreeAgent (Settings → Construction Industry Scheme).');
      }
    }
    const categoryColumns = {
      labour_category_url: categories.labour || null,
      materials_category_url: categories.materials || null,
      other_category_url: categories.other || null,
    };

    const markError = async (message: string) => {
      await supabase
        .from('account_bills')
        .update({ status: 'error', sync_error: message, last_synced_at: new Date().toISOString() })
        .eq('id', bill.id);
    };

    let billUrl = bill.freeagentBillUrl;
    try {
      const { contactUrl } = await ensureFreeAgentAccountContact(account.id);
      if (bill.cisBand !== 'none') {
        await freeAgentApi.updateContactCis(contactUrl, {
          band: FREEAGENT_CIS_BANDS[bill.cisBand],
          utr: account.utrNumber,
          verificationNumber: account.cisVerificationNumber,
        });
      }

      if (billUrl) {
        const current = await freeAgentApi.getBill(billUrl);
        const existingItemUrls = (current.bill_items || []).map((item) => item.url).filter(Boolean);
        await freeAgentApi.updateBill(billUrl, buildFreeAgentBillUpdatePayload({ bill, contactUrl, categories, existingItemUrls }));
      } else {
        const attachment = await this.freeAgentAttachment(bill);
        const payload = buildFreeAgentBillPayload({ bill, contactUrl, categories, attachment });
        billUrl = await freeAgentApi.createBill(payload);
        // Record the URL straight away so the bill can never be created twice.
        const { error } = await supabase
          .from('account_bills')
          .update({ status: 'sent', freeagent_bill_url: billUrl, sync_error: null, ...categoryColumns })
          .eq('id', bill.id);
        if (error) {
          throw new Error(`The bill was created in FreeAgent (${billUrl}) but JARVIS could not record it: ${error.message}. Do not send it again.`);
        }
      }
    } catch (err) {
      const message = errorMessage(err, 'FreeAgent did not accept the bill');
      if (!/created in FreeAgent/.test(message)) await markError(message);
      throw new Error(message);
    }

    const saved = await updateRow(bill.id, {
      status: 'sent',
      sync_error: null,
      freeagent_out_of_date: false,
      last_synced_at: new Date().toISOString(),
      ...categoryColumns,
    }, 'The bill is in FreeAgent but JARVIS could not record it');
    return this.refreshFromFreeAgent(saved.id);
  },

  /** The scanned supplier invoice if there is one, otherwise our PDF of the bill. */
  async freeAgentAttachment(bill: AccountBill): Promise<{ base64: string; fileName: string; contentType: string } | null> {
    if (bill.attachmentPath) {
      const { data, error } = await supabase.storage.from(BILL_FILES_BUCKET).download(bill.attachmentPath);
      if (error || !data) throw new Error(errorMessage(error, 'Failed to read the attachment'));
      return {
        base64: await blobToBase64(data),
        fileName: bill.attachmentName || 'supplier-invoice',
        contentType: bill.attachmentContentType || data.type,
      };
    }
    try {
      const blob = await this.pdfBlob(bill);
      if (blob.size > MAX_BILL_ATTACHMENT_BYTES) return null;
      return { base64: await blobToBase64(blob), fileName: `Invoice ${bill.reference}.pdf`, contentType: 'application/pdf' };
    } catch {
      // The bill is still worth sending without a document
      return null;
    }
  },

  /** Reads the bill back from FreeAgent: payment, and its CIS deduction checked against ours. */
  async refreshFromFreeAgent(billId: string): Promise<AccountBill> {
    const bill = await this.get(billId);
    if (!bill) throw new Error('Bill not found');
    if (!bill.freeagentBillUrl) return bill;
    await this.assertFreeAgentReady();
    let remote: freeAgentApi.FreeAgentBill;
    try {
      remote = await freeAgentApi.getBill(bill.freeagentBillUrl);
    } catch (err) {
      if (isNotFound(err)) {
        return updateRow(bill.id, {
          status: 'draft',
          freeagent_bill_url: null,
          freeagent_cis_deduction: null,
          freeagent_out_of_date: false,
          sync_error: 'The bill was deleted in FreeAgent. Send it again if it is still owed.',
          last_synced_at: new Date().toISOString(),
        }, 'Failed to update the bill');
      }
      throw new Error(errorMessage(err, 'Could not read the bill from FreeAgent'));
    }
    const mismatch = cisDeductionMismatch(bill, remote.cis_deduction);
    const paidOn = remote.paid_on || (String(remote.status || '').toLowerCase() === 'paid' ? bill.paidOn || today() : null);
    return updateRow(bill.id, {
      status: mismatch ? 'error' : 'sent',
      sync_error: mismatch,
      freeagent_cis_deduction: remote.cis_deduction != null && remote.cis_deduction !== '' ? Number(remote.cis_deduction) : null,
      paid_on: paidOn,
      last_synced_at: new Date().toISOString(),
    }, 'Failed to update the bill');
  },
};
